import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Alert,
  IconButton,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Product, Category, BlogPost, Testimonial } from '../../services/FirestoreService';
import {
  getProducts,
  getCategories,
  getBlogPosts,
  getSliders,
  getTestimonials,
  getSiteSettings,
  updateSiteSettings,
  addDoc,
  collection,
  doc,
  updateDoc,
  deleteDoc
} from '../../services/FirestoreService';
import { db } from '../../firebase/firebaseConfig';
import { Order, getUserOrders, updateOrderStatus, getAllOrders } from '../../services/OrderService';
import {
  getCoupons,
  addCoupon,
  getFAQs,
  addDoc as addAdditionalDoc,
  collection as additionalCollection,
  doc as additionalDoc,
  updateDoc as updateAdditionalDoc,
  deleteDoc as deleteAdditionalDoc
} from '../../services/AdditionalFirestoreService';

interface Slider {
  id: string;
  title: string;
  image: string;
  categoryId: string; // Add category ID to link slider to category
  category: string; // Add category name for display
  isActive: boolean;
}

interface Coupon {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expirationDate: Date;
  isActive: boolean;
  usageLimit?: number;
  usedCount?: number;
  createdAt?: Date | any; // Added to handle Firestore timestamp
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  isActive: boolean;
}

interface SiteSettings {
  id: string;
  siteTitle: string;
  siteDescription: string;
  logoUrl: string;
  faviconUrl: string;
  freeShippingThreshold: number;
  orderProcessingTime: string; // e.g., "2-3 iş günü"
  contactEmail: string;
  contactPhone: string;
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newProduct, setNewProduct] = useState({
    name: '', price: '', discountedPrice: '', stock: 0, category: '', image: '', isActive: true
  });
  const [newCategory, setNewCategory] = useState({ name: '', image: '', isActive: true });
  const [newSlider, setNewSlider] = useState({ title: '', image: '', categoryId: '', category: '', isActive: true });
  const [newTestimonial, setNewTestimonial] = useState({ name: '', comment: '', rating: 5, isActive: true });
  const [newCoupon, setNewCoupon] = useState({
    code: '', discountType: 'percentage' as 'percentage' | 'fixed', discountValue: 0, 
    expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    isActive: true, usageLimit: 0
  });
  const [newFaq, setNewFaq] = useState({
    question: '', answer: '', category: '', isActive: true
  });

  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Load products, categories, etc. from Firestore
        const [fetchedProducts, fetchedCategories, allOrders, fetchedSliders, fetchedSettings, fetchedTestimonials, fetchedCoupons, fetchedFaqs] = await Promise.all([
          getProducts(),
          getCategories(),
          getAllOrders(),
          getSliders(),
          getSiteSettings(),
          getTestimonials(),
          getCoupons(),
          getFAQs()
        ]);

        setProducts(fetchedProducts);
        setCategories(fetchedCategories);
        setOrders(allOrders);
        setSliders(fetchedSliders);
        setSettings(fetchedSettings);
        setTestimonials(fetchedTestimonials);
        setCoupons(fetchedCoupons);
        setFaqs(fetchedFaqs);
      } catch (error) {
        console.error('Error fetching data:', error);
        setMessage('Veri yüklenirken bir hata oluştu: ' + (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <CircularProgress />
      </Container>
    );
  }

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      setMessage('Lütfen gerekli alanları doldurun');
      return;
    }

    try {
      const productData = {
        name: newProduct.name,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock.toString()) || 0,
        category: newProduct.category,
        image: newProduct.image || '',
        discountedPrice: newProduct.discountedPrice ? parseFloat(newProduct.discountedPrice) : null, // Set to null if no discount
        rating: 0,
        discount: 0,
        isNew: false,
        description: '',
        features: []
      };

      const docRef = await addDoc(collection(db, 'products'), productData);

      setProducts([...products, { ...productData, id: docRef.id }]);
      setNewProduct({ name: '', price: '', discountedPrice: '', stock: 0, category: '', image: '', isActive: true });
      setMessage('Ürün başarıyla eklendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding product:', error);
      setMessage('Ürün eklenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'products', id));
      setProducts(products.filter(product => product.id !== id));
      setMessage('Ürün başarıyla silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage('Ürün silinirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      setMessage('Lütfen kategori adını girin');
      return;
    }

    try {
      const categoryData = {
        name: newCategory.name,
        image: newCategory.image,
        isActive: newCategory.isActive
      };

      const docRef = await addDoc(collection(db, 'categories'), categoryData);

      setCategories([...categories, { ...categoryData, id: docRef.id }]);
      setNewCategory({ name: '', image: '', isActive: true });
      setMessage('Kategori başarıyla eklendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding category:', error);
      setMessage('Kategori eklenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'categories', id));
      setCategories(categories.filter(category => category.id !== id));
      setMessage('Kategori başarıyla silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting category:', error);
      setMessage('Kategori silinirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleAddSlider = async () => {
    if (!newSlider.title || !newSlider.image || !newSlider.categoryId) {
      setMessage('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      // Find the selected category to get its name
      const selectedCategory = categories.find(cat => cat.id === newSlider.categoryId);
      const sliderData = {
        title: newSlider.title,
        image: newSlider.image,
        categoryId: newSlider.categoryId,
        category: selectedCategory ? selectedCategory.name : '',
        isActive: newSlider.isActive
      };

      const docRef = await addDoc(collection(db, 'sliders'), sliderData);

      setSliders([...sliders, { ...sliderData, id: docRef.id }]);
      setNewSlider({ title: '', image: '', categoryId: '', category: '', isActive: true });
      setMessage('Slider başarıyla eklendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding slider:', error);
      setMessage('Slider eklenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleDeleteSlider = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'sliders', id));
      setSliders(sliders.filter(slider => slider.id !== id));
      setMessage('Slider başarıyla silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting slider:', error);
      setMessage('Slider silinirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleAddTestimonial = async () => {
    if (!newTestimonial.name || !newTestimonial.comment) {
      setMessage('Lütfen gerekli alanları doldurun');
      return;
    }

    try {
      const testimonialData = {
        name: newTestimonial.name,
        comment: newTestimonial.comment,
        rating: newTestimonial.rating,
        isActive: newTestimonial.isActive
      };

      const docRef = await addDoc(collection(db, 'testimonials'), testimonialData);

      setTestimonials([...testimonials, { ...testimonialData, id: docRef.id }]);
      setNewTestimonial({ name: '', comment: '', rating: 5, isActive: true });
      setMessage('Yorum başarıyla eklendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding testimonial:', error);
      setMessage('Yorum eklenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleDeleteTestimonial = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'testimonials', id));
      setTestimonials(testimonials.filter(testimonial => testimonial.id !== id));
      setMessage('Yorum başarıyla silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      setMessage('Yorum silinirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleUpdateOrderStatus = async (orderId: string, newStatus: Order['status']) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      // Update the order in the local state
      setOrders(orders.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      setMessage('Sipariş durumu güncellendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating order status:', error);
      setMessage('Sipariş durumu güncellenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleUpdateSettings = async () => {
    if (!settings) return;

    try {
      await updateSiteSettings({
        siteTitle: settings.siteTitle,
        siteDescription: settings.siteDescription,
        logoUrl: settings.logoUrl,
        faviconUrl: settings.faviconUrl,
        freeShippingThreshold: settings.freeShippingThreshold,
        orderProcessingTime: settings.orderProcessingTime,
        contactEmail: settings.contactEmail,
        contactPhone: settings.contactPhone
      });

      setMessage('Site ayarları başarıyla güncellendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating settings:', error);
      setMessage('Ayarlar güncellenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  // Coupon management functions
  const handleAddCoupon = async () => {
    if (!newCoupon.code) {
      setMessage('Lütfen kupon kodunu girin');
      return;
    }

    try {
      const couponData = {
        code: newCoupon.code.toUpperCase(), // Convert to uppercase for consistency
        discountType: newCoupon.discountType,
        discountValue: newCoupon.discountValue,
        expirationDate: new Date(newCoupon.expirationDate),
        isActive: newCoupon.isActive,
        usageLimit: newCoupon.usageLimit && newCoupon.usageLimit > 0 ? newCoupon.usageLimit : undefined,
        usedCount: 0
      };

      const docRef = await addAdditionalDoc(additionalCollection(db, 'coupons'), couponData);

      setCoupons([...coupons, { ...couponData, id: docRef.id }]);
      setNewCoupon({
        code: '', 
        discountType: 'percentage', 
        discountValue: 0, 
        expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        isActive: true, 
        usageLimit: 0
      });
      setMessage('Kupon başarıyla eklendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding coupon:', error);
      setMessage('Kupon eklenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleDeleteCoupon = async (id: string) => {
    try {
      await deleteAdditionalDoc(additionalDoc(db, 'coupons', id));
      setCoupons(coupons.filter(coupon => coupon.id !== id));
      setMessage('Kupon başarıyla silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting coupon:', error);
      setMessage('Kupon silinirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  // FAQ management functions
  const handleAddFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      setMessage('Lütfen soru ve cevabı girin');
      return;
    }

    try {
      const faqData = {
        question: newFaq.question,
        answer: newFaq.answer,
        category: newFaq.category || 'General',
        isActive: newFaq.isActive
      };

      const docRef = await addAdditionalDoc(additionalCollection(db, 'faqs'), faqData);

      setFaqs([...faqs, { ...faqData, id: docRef.id }]);
      setNewFaq({ question: '', answer: '', category: '', isActive: true });
      setMessage('Sık Sorulan Soru başarıyla eklendi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding FAQ:', error);
      setMessage('SSS eklenirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  const handleDeleteFaq = async (id: string) => {
    try {
      await deleteAdditionalDoc(additionalDoc(db, 'faqs', id));
      setFaqs(faqs.filter(faq => faq.id !== id));
      setMessage('SSS başarıyla silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      setMessage('SSS silinirken bir hata oluştu: ' + (error as Error).message);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Paneli
      </Typography>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Ürün Yönetimi" />
          <Tab label="Kategori Yönetimi" />
          <Tab label="Slider Yönetimi" />
          <Tab label="Yorum Yönetimi" />
          <Tab label="Siparişler" />
          <Tab label="Kullanıcılar" />
          <Tab label="Site Ayarları" />
          <Tab label="Kuponlar" />
          <Tab label="SSS" />
        </Tabs>
      </Paper>

      {/* Products Management Tab */}
      {activeTab === 0 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yeni Ürün Ekle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Ürün Adı"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Fiyat"
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Stok"
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({...newProduct, stock: parseInt(e.target.value) || 0})}
                  sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel id="category-select-label">Kategori</InputLabel>
                  <Select
                    labelId="category-select-label"
                    id="category-select"
                    value={newProduct.category}
                    label="Kategori"
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.name}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="İndirimli Fiyat"
                  type="number"
                  value={newProduct.discountedPrice}
                  onChange={(e) => setNewProduct({...newProduct, discountedPrice: e.target.value})}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Görsel URL"
                  value={newProduct.image}
                  onChange={(e) => setNewProduct({...newProduct, image: e.target.value})}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProduct}
                  sx={{ alignSelf: 'flex-end', mb: 1 }}
                >
                  Ürün Ekle
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ürün Listesi
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Adı</TableCell>
                      <TableCell>Fiyat</TableCell>
                      <TableCell>Stok</TableCell>
                      <TableCell>Kategori</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.id}</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>₺{product.price}</TableCell>
                        <TableCell>{product.stock}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Categories Management Tab */}
      {activeTab === 1 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yeni Kategori Ekle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Kategori Adı"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Görsel URL"
                  value={newCategory.image}
                  onChange={(e) => setNewCategory({...newCategory, image: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddCategory}
                  sx={{ alignSelf: 'flex-end', mb: 1 }}
                >
                  Kategori Ekle
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kategori Listesi
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Adı</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell>{category.id}</TableCell>
                        <TableCell>{category.name}</TableCell>
                        <TableCell>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Slider Management Tab */}
      {activeTab === 2 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yeni Slider Ekle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Başlık"
                  value={newSlider.title}
                  onChange={(e) => setNewSlider({...newSlider, title: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Görsel URL"
                  value={newSlider.image}
                  onChange={(e) => setNewSlider({...newSlider, image: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel id="slider-category-select-label">Kategori</InputLabel>
                  <Select
                    labelId="slider-category-select-label"
                    id="slider-category-select"
                    value={newSlider.categoryId}
                    label="Kategori"
                    onChange={(e) => setNewSlider({...newSlider, categoryId: e.target.value, category: categories.find(cat => cat.id === e.target.value)?.name || ''})}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSlider}
                  sx={{ alignSelf: 'flex-end', mb: 1 }}
                >
                  Slider Ekle
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Slider Listesi
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Başlık</TableCell>
                      <TableCell>Kategori</TableCell>
                      <TableCell>Görsel</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sliders.map((slider) => (
                      <TableRow key={slider.id}>
                        <TableCell>{slider.id}</TableCell>
                        <TableCell>{slider.title}</TableCell>
                        <TableCell>{slider.category}</TableCell>
                        <TableCell>
                          <img
                            src={slider.image}
                            alt={slider.title}
                            style={{ width: '100px', height: '50px', objectFit: 'cover' }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteSlider(slider.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Testimonials Management Tab */}
      {activeTab === 3 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yeni Yorum Ekle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Ad Soyad"
                  value={newTestimonial.name}
                  onChange={(e) => setNewTestimonial({...newTestimonial, name: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Yorum"
                  value={newTestimonial.comment}
                  onChange={(e) => setNewTestimonial({...newTestimonial, comment: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Puan"
                  type="number"
                  value={newTestimonial.rating}
                  onChange={(e) => setNewTestimonial({...newTestimonial, rating: parseInt(e.target.value) || 5})}
                  sx={{ mb: 1, width: '100px' }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddTestimonial}
                  sx={{ alignSelf: 'flex-end', mb: 1 }}
                >
                  Yorum Ekle
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yorum Listesi
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Ad Soyad</TableCell>
                      <TableCell>Yorum</TableCell>
                      <TableCell>Puan</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testimonials.map((testimonial) => (
                      <TableRow key={testimonial.id}>
                        <TableCell>{testimonial.id}</TableCell>
                        <TableCell>{testimonial.name}</TableCell>
                        <TableCell>{testimonial.comment}</TableCell>
                        <TableCell>{testimonial.rating}</TableCell>
                        <TableCell>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteTestimonial(testimonial.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Orders Tab */}
      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sipariş Yönetimi
            </Typography>

            {orders.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Kullanıcı</TableCell>
                      <TableCell>Tarih</TableCell>
                      <TableCell>Toplam</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.id}</TableCell>
                        <TableCell>{order.userId.substring(0, 8)}...</TableCell>
                        <TableCell>{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>₺{order.total.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            color={
                              order.status === 'delivered' ? 'success' :
                              order.status === 'cancelled' ? 'error' :
                              order.status === 'pending' ? 'default' :
                              'primary'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small">
                            <Select
                              value={order.status}
                              onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value as any)}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="pending">Beklemede</MenuItem>
                              <MenuItem value="processing">İşleniyor</MenuItem>
                              <MenuItem value="shipped">Gönderildi</MenuItem>
                              <MenuItem value="delivered">Teslim Edildi</MenuItem>
                              <MenuItem value="cancelled">İptal Edildi</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>Sipariş bulunmamaktadır.</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Users Tab */}
      {activeTab === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Kullanıcı Yönetimi
            </Typography>
            <Typography variant="body1">
              Bu bölümde kullanıcıları görüntüleyebilir, rollerini yönetebilir ve istatistikleri takip edebilirsiniz.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 6 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Site Ayarları
            </Typography>

            {settings ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Site Başlığı"
                  value={settings.siteTitle}
                  onChange={(e) => setSettings({...settings, siteTitle: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Site Açıklaması"
                  value={settings.siteDescription}
                  onChange={(e) => setSettings({...settings, siteDescription: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Logo URL"
                  value={settings.logoUrl}
                  onChange={(e) => setSettings({...settings, logoUrl: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Favicon URL"
                  value={settings.faviconUrl}
                  onChange={(e) => setSettings({...settings, faviconUrl: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Ücretsiz Kargo Alt Sınırı (₺)"
                  type="number"
                  value={settings.freeShippingThreshold}
                  onChange={(e) => setSettings({...settings, freeShippingThreshold: parseFloat(e.target.value) || 0})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Sipariş Hazırlama Süresi"
                  value={settings.orderProcessingTime}
                  onChange={(e) => setSettings({...settings, orderProcessingTime: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="İletişim E-posta"
                  value={settings.contactEmail}
                  onChange={(e) => setSettings({...settings, contactEmail: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="İletişim Telefon"
                  value={settings.contactPhone}
                  onChange={(e) => setSettings({...settings, contactPhone: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleUpdateSettings}
                  sx={{ mt: 2, alignSelf: 'flex-start' }}
                >
                  Ayarları Güncelle
                </Button>
              </Box>
            ) : (
              <Typography>Ayarlar yükleniyor...</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Coupons Tab */}
      {activeTab === 7 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yeni Kupon Ekle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Kupon Kodu"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value})}
                  sx={{ mb: 1 }}
                />
                <FormControl sx={{ mb: 1, minWidth: 120 }}>
                  <InputLabel id="discount-type-select-label">İndirim Türü</InputLabel>
                  <Select
                    labelId="discount-type-select-label"
                    id="discount-type-select"
                    value={newCoupon.discountType}
                    label="İndirim Türü"
                    onChange={(e) => setNewCoupon({...newCoupon, discountType: e.target.value as 'percentage' | 'fixed'})}
                  >
                    <MenuItem value="percentage">Yüzde</MenuItem>
                    <MenuItem value="fixed">Sabit Tutar</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="İndirim Değeri"
                  type="number"
                  value={newCoupon.discountValue}
                  onChange={(e) => setNewCoupon({...newCoupon, discountValue: parseFloat(e.target.value) || 0})}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Kullanım Limiti"
                  type="number"
                  value={newCoupon.usageLimit}
                  onChange={(e) => setNewCoupon({...newCoupon, usageLimit: parseInt(e.target.value) || 0})}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Son Kullanma Tarihi"
                  type="date"
                  value={newCoupon.expirationDate.toISOString().split('T')[0]}
                  onChange={(e) => setNewCoupon({...newCoupon, expirationDate: new Date(e.target.value)})}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddCoupon}
                  sx={{ alignSelf: 'flex-end', mb: 1 }}
                >
                  Kupon Ekle
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kupon Listesi
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kod</TableCell>
                      <TableCell>Tip</TableCell>
                      <TableCell>Değer</TableCell>
                      <TableCell>Son Tarih</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Kullanım</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {coupons.map((coupon) => (
                      <TableRow key={coupon.id}>
                        <TableCell>{coupon.code}</TableCell>
                        <TableCell>{coupon.discountType === 'percentage' ? 'Yüzde' : 'Sabit'}</TableCell>
                        <TableCell>{coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `₺${coupon.discountValue}`}</TableCell>
                        <TableCell>{coupon.expirationDate ? new Date(coupon.expirationDate).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={coupon.isActive ? 'Aktif' : 'Pasif'}
                            color={coupon.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {coupon.usedCount || 0}{coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* FAQs Tab */}
      {activeTab === 8 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yeni Sık Sorulan Soru Ekle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Soru"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq({...newFaq, question: e.target.value})}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Cevap"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Kategori"
                  value={newFaq.category}
                  onChange={(e) => setNewFaq({...newFaq, category: e.target.value})}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddFaq}
                  sx={{ alignSelf: 'flex-end', mb: 1 }}
                >
                  SSS Ekle
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sık Sorulan Sorular Listesi
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Soru</TableCell>
                      <TableCell>Kategori</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {faqs.map((faq) => (
                      <TableRow key={faq.id}>
                        <TableCell>{faq.question}</TableCell>
                        <TableCell>{faq.category}</TableCell>
                        <TableCell>
                          <Chip
                            label={faq.isActive ? 'Aktif' : 'Pasif'}
                            color={faq.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteFaq(faq.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default AdminPanel;